import type {
  ColDef,
  ColGroupDef,
  ICellRendererParams
} from 'ag-grid-community';

import { textRenderer } from '@/utils/GridRenderer.utils';

import {
  AdminDeviceNameCellRenderer,
  AdminDeviceSourceCountCellRenderer,
  AdminDeviceStatusCellRenderer
} from './components';
import { ipAddressComparator } from './utils/adminTreeData.utils';

/**
 * Creates column definitions for admin devices table
 *
 * Generates AG Grid column configuration for the admin devices view.
 * Conditionally includes organization column when viewing all organizations.
 *
 * @param isMainDevicePage - Whether this is the main devices page (shows action indicators)
 * @param selectedOrganizationId - Currently selected organization ID (0 = all orgs)
 * @returns Array of column definitions for AG Grid
 *
 * @example
 * ```tsx
 * const columnDefs = createAdminDeviceColumnDefs(true, 1);
 * <AgGridReact columnDefs={columnDefs} />
 * ```
 */
export const createAdminDeviceColumnDefs = (
  isMainDevicePage: boolean,
  selectedOrganizationId: number
): (ColDef | ColGroupDef)[] => {
  const columns: (ColDef | ColGroupDef)[] = [
    {
      field: 'host_name',
      headerName: 'Device Name',
      minWidth: 250,
      maxWidth: 350,
      width: 350,
      cellClass: 'ellipsis-cell',
      cellRenderer:
        AdminDeviceNameCellRenderer as React.ComponentType<ICellRendererParams>
    },
    {
      field: 'ip_address',
      headerName: 'IP Address',
      flex: 1,
      minWidth: 150,
      comparator: ipAddressComparator,
      cellRenderer: textRenderer
    },
    {
      field: 'status',
      headerName: 'Status',
      cellRenderer:
        AdminDeviceStatusCellRenderer as React.ComponentType<ICellRendererParams>,
      cellRendererParams: {
        isMainDevicePage
      },
      flex: 1,
      minWidth: 150
    },
    {
      field: 'last_seen_display',
      headerName: 'Last Seen',
      cellRenderer: textRenderer,
      flex: 1,
      minWidth: 150,
      sort: 'desc'
    },
    {
      field: 'source_count',
      headerName: 'Sources',
      cellRenderer:
        AdminDeviceSourceCountCellRenderer as React.ComponentType<ICellRendererParams>,
      flex: 0.5,
      minWidth: 100
    }
  ];

  // Add organization column if viewing all organizations
  if (isMainDevicePage && selectedOrganizationId === 0) {
    columns.splice(4, 0, {
      field: 'organization.label',
      headerName: 'Organization',
      cellRenderer: textRenderer,
      flex: 1,
      minWidth: 150
    });
  }

  return columns;
};
